import { XMarkIcon } from "@heroicons/react/20/solid";
import { useEffect, useState, useMemo } from "react";
import { api } from "../api";
import { COUNTRY_PHONE_CODES_MAP } from "../data/country-phone-codes";
import { Button } from "./tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "./tailwind/dialog";
import { Field, Legend } from "./tailwind/fieldset";
import AutocompleteInput from "./tailwind/autocomplete-input";

const CountryFilterModal = ({
  campaign,
  isModalCountryFilterOpen,
  setIsModalCountryFilterOpen,
}) => {
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [otherCampaigns, setOtherCampaigns] = useState([]);

  useEffect(() => {
    if (isModalCountryFilterOpen) {
      if (campaign?.phoneCountryCodeList) {
        const initialCountries = campaign.phoneCountryCodeList
          .map((code) => {
            const found = COUNTRY_PHONE_CODES_MAP.find((c) => c.value === code);
            return found;
          })
          .filter(Boolean);
        setSelectedCountries(initialCountries);
      } else {
        setSelectedCountries([]);
      }

      const fetchOtherCampaigns = async () => {
        try {
          const campaigns = await api.campaign.findMany({
            filter: {
              customerId: { equals: campaign.customerId },
              automationType: { equals: campaign.automationType },
              isArchived: { notEquals: true },
              isAutomationActive: { equals: true },
              id: { notEquals: campaign.id },
            },
            select: {
              id: true,
              name: true,
              phoneCountryCodeList: true,
            },
          });
          setOtherCampaigns(campaigns);
        } catch (error) {
          console.error("Failed to fetch other campaigns:", error);
        }
      };

      fetchOtherCampaigns();
    }
  }, [campaign, isModalCountryFilterOpen]);

  const handleSelectCountry = (option) => {
    if (!option || !option.value || option.disabled) return;

    const isAlreadySelected = selectedCountries.some(
      (country) => country.value === option.value,
    );

    if (!isAlreadySelected) {
      setSelectedCountries([...selectedCountries, option]);
    }
    setSearchValue("");
  };

  const handleSearchChange = (e) => {
    let value;
    if (e) {
      value = typeof e === "string" ? e : e.target.value;
    } else {
      value = "";
    }
    setSearchValue(value);
  };

  const handleRemoveCountry = (countryToRemove) => {
    setSelectedCountries(
      selectedCountries.filter(
        (country) => country.value !== countryToRemove.value,
      ),
    );
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setValidationError("");
      const countryCodeList = selectedCountries.map((country) => country.value);

      if (countryCodeList.length === 0 && campaign.isAutomationActive) {
        const fallbackCampaign = await api.campaign.maybeFindFirst({
          filter: {
            customerId: { equals: campaign.customerId },
            automationType: { equals: campaign.automationType },
            isArchived: { notEquals: true },
            isAutomationActive: { equals: true },
            id: { notEquals: campaign.id },
            OR: [
              { phoneCountryCodeList: { equals: [] } },
              { phoneCountryCodeList: { equals: null } },
            ],
          },
          select: {
            name: true,
          },
        });

        if (fallbackCampaign) {
          setValidationError(
            `Only one automation without country filter is allowed. "${fallbackCampaign.name}" already has no country filter.`,
          );
          setIsSaving(false);
          return;
        }
      }

      await api.campaign.update(campaign.id, {
        phoneCountryCodeList: countryCodeList,
      });
      setIsModalCountryFilterOpen(false);
    } catch (error) {
      console.error("Failed to update campaign:", error);
      setValidationError("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (campaign?.phoneCountryCodeList) {
      const initialCountries = campaign.phoneCountryCodeList
        .map((code) => COUNTRY_PHONE_CODES_MAP.find((c) => c.value === code))
        .filter(Boolean);
      setSelectedCountries(initialCountries);
    } else {
      setSelectedCountries([]);
    }
    setSearchValue("");
    setValidationError("");
    setIsModalCountryFilterOpen(false);
  };
  const availableOptions = useMemo(() => {
    const usedCodes = new Set();
    otherCampaigns.forEach((campaign) => {
      if (campaign.phoneCountryCodeList) {
        campaign.phoneCountryCodeList.forEach((code) => usedCodes.add(code));
      }
    });

    return COUNTRY_PHONE_CODES_MAP.filter(
      (option) =>
        !selectedCountries.some((selected) => selected.value === option.value),
    ).map((option) => {
      if (usedCodes.has(option.value)) {
        return {
          ...option,
          label: `${option.label} (Already used)`,
          disabled: true,
        };
      }
      return option;
    });
  }, [selectedCountries, otherCampaigns]);
  return (
    <Dialog
      key="dialog_country_filter"
      open={isModalCountryFilterOpen}
      onClose={handleCancel}
    >
      <DialogTitle>Select one or multiple countries</DialogTitle>
      <DialogBody>
        <p className="mb-2">
          Send this automation to customers whose dialing code is:
        </p>
        {selectedCountries.length > 0 && (
          <Field className="mb-4">
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedCountries.map((country) => (
                <div
                  key={country.value}
                  className="flex items-center gap-2 bg-purple/10 text-purple px-3 py-1 rounded-full"
                >
                  <span>{country.label}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveCountry(country)}
                    className="hover:bg-purple/20 rounded-full p-0.5"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </Field>
        )}

        <Field>
          <Legend>Add countries</Legend>
          <AutocompleteInput
            value={searchValue}
            onChange={handleSearchChange}
            onSelect={handleSelectCountry}
            options={availableOptions}
            selected={null}
          />
          <p className="mt-2 text-xs italic text-gray-600">
            Automations will be blocked for the selected country codes. Please
            note that this restriction also applies if the user provides their
            BSUID instead of a phone number.
          </p>
        </Field>
        {validationError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{validationError}</p>
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          color="primaryButton"
          className="flex-shrink-0"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CountryFilterModal;
